import { Close as CloseIcon, Menu as MenuIcon, ShoppingBasketOutlined } from '@mui/icons-material';
import { Badge, Box, Drawer, IconButton, MenuItem, Select } from '@mui/material';
import React, { useContext, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { SCHOOLS_PATH, SCHOOL_GROUPS_PATH, userNtfNewMenuPath, userNtfNewPackagesPath, userProfilePath, userSchoolsPath } from "../../../functions/src/admin/paths";
import { auth, currentDb, DataSnapshot, mainDatabase, schoolDatabase } from "../../api/firebase";
import { useDatabase, useDatabaseVal, useList } from "../../api/firebaseHooks";
import { useBalance } from "../../api/funds";
import { langContext, LangToggle, useSchoolLangs } from "../../api/Lang";
import { useStrings } from "../../common-strings";
import { formatPriceEuro } from "../../api/priceFormat";
import { Schools, UserProfile } from "../../types";
import { useNavigationVisibility } from '../useNavigationVisibility';
import { useNewsUnread } from '../news/useNewsUnread';
import { useNewsStrings } from '../news/news-strings';
import { useAppLayoutStrings } from './appLayout-strings';
import { isProfileMenu, profileHash } from '../profile/profileTabs';
import type { NavigatorOption } from './Navigator';
import { useMonthlyMenuStrings } from '../userPackages/monthlyMenu-strings';
import { useBasketContext } from '../basket/BasketProvider';
import { useBasketStrings } from '../basket/basket-strings';
import GefsinusNavPanel from './GefsinusNavPanel';
import { usePlatformSupportStrings } from '../platform-support-strings';

import OkDlg from "../../api/OkDlg";

type MobileNavItem = {
    key: string;
    label: string;
    icon: string;
    badgeCount?: number;
    /** “New” pill (packages / monthly menu), same as desktop navigator */
    showNewBadge?: boolean;
};
import YesNoDlg from "../../api/YesNoDlg";

// import '../../src/admin/admin-app.css';

type HeaderProps = {
    onRefill: () => void;
    onNavigationChange: (menu: NavigatorOption, shouldScrollToTop?: boolean) => void;
    currentSelection: string;
};

function navSectionKey(selection: string | null | undefined): string {
    if (selection == null || selection === '') {
        return 'order';
    }
    return isProfileMenu(selection) || selection === 'profile' ? 'profile' : selection;
}

export function Header({ onRefill, onNavigationChange, currentSelection }: HeaderProps) {
    const balance = useBalance();
    const showHeaderBalance = balance !== 0;
    const uid = auth.currentUser?.uid;
    const photoURL = auth.currentUser?.photoURL;
    const [profile] = useDatabaseVal<UserProfile>(uid ? userProfilePath(uid) : '');

    const isMobile = useMediaQuery({ query: '(max-width: 930px)' });
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const [userSchools] = useDatabaseVal<{ [key: string]: boolean }>(uid ? userSchoolsPath(uid) : '');
    const allSchools: DataSnapshot<Schools[]> = useDatabase(SCHOOLS_PATH);
    const allSchoolsList: DataSnapshot<Schools>[] = useList(allSchools);
    const schoolGroups: DataSnapshot<{ [key: string]: { [key: string]: boolean } }> = useDatabase(SCHOOL_GROUPS_PATH);

    const [lang, setLang] = useContext(langContext);
    const { langs: applicableLangs, loaded: schoolLangsLoaded } = useSchoolLangs();
    const showLangToggle = schoolLangsLoaded && applicableLangs.length > 1;
    const [schoolToRemove, setSchoolToRemove] = useState<{ key: string, name: string } | null>(null);
    const [isSelectOpen, setIsSelectOpen] = useState(false);
    const [isMobileSelectOpen, setIsMobileSelectOpen] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [errorType, setErrorType] = useState<'network' | 'readded' | null>(null);
    const selectRef = useRef<any>(null);

    // Get navigation visibility
    const { shouldShowPackages, shouldShowMonthlyMenu } = useNavigationVisibility();
    const { entries: basketEntries, totalBasketDays, openBasket } = useBasketContext();
    const basketStrings = useBasketStrings();
    const commonStrings = useStrings();
    const layoutStrings = useAppLayoutStrings();
    const monthlyStrings = useMonthlyMenuStrings();
    const newsStrings = useNewsStrings();
    const platformSupportStrings = usePlatformSupportStrings();
    const { unreadCount: unreadNewsCount } = useNewsUnread(uid);
    const hasSeenNewPackages = useDatabase(uid ? userNtfNewPackagesPath(uid) : null)?.val() === true;
    const hasSeenNewMenu = useDatabase(uid ? userNtfNewMenuPath(uid) : null)?.val() === true;

    const showPackagesNavNtf = shouldShowPackages && !hasSeenNewPackages;
    const showMonthlyMenuNavNtf = shouldShowMonthlyMenu && !hasSeenNewMenu;
    const hasMobileNavNotifications = showPackagesNavNtf || showMonthlyMenuNavNtf || unreadNewsCount > 0;

    // Navigation items for mobile menu - conditionally include packages and monthly menu
    const navigationItems: MobileNavItem[] = useMemo(() => [
        { key: 'order', label: layoutStrings.navOrder, icon: '/img-assets/order.svg' },
        ...(shouldShowPackages ? [{
            key: 'packages',
            label: layoutStrings.navPackages,
            icon: '/img-assets/packages.svg',
            showNewBadge: !hasSeenNewPackages,
        }] : []),
        ...(shouldShowMonthlyMenu ? [{
            key: 'monthlyMenu',
            label: monthlyStrings.navLabel,
            icon: '/img-assets/menu.svg',
            showNewBadge: !hasSeenNewMenu,
        }] : []),
        { key: 'profile', label: commonStrings.navMyAccount, icon: '/img-assets/account.svg' },
        { key: 'news', label: newsStrings.navLabel, icon: '/img-assets/news.svg', badgeCount: unreadNewsCount },
        { key: 'settings', label: commonStrings.navSettings, icon: '/img-assets/settings.svg' },
        {
            key: 'platformSupport',
            label: platformSupportStrings.navLabel,
            icon: '/img-assets/platform-support.svg',
        },
    ], [
        layoutStrings.navOrder,
        layoutStrings.navPackages,
        monthlyStrings.navLabel,
        commonStrings.navMyAccount,
        commonStrings.navSettings,
        platformSupportStrings.navLabel,
        newsStrings.navLabel,
        shouldShowPackages,
        shouldShowMonthlyMenu,
        hasSeenNewPackages,
        hasSeenNewMenu,
        unreadNewsCount,
    ]);

    const handleNavigationClick = (navKey: string) => {
        if (uid) {
            if (navKey === 'packages') {
                void schoolDatabase.ref(userNtfNewPackagesPath(uid)).set(true).catch((err) => console.warn('Could not clear packages nav notification flag:', err));
            } else if (navKey === 'monthlyMenu') {
                void schoolDatabase.ref(userNtfNewMenuPath(uid)).set(true).catch((err) => console.warn('Could not clear monthly menu nav notification flag:', err));
            }
        }
        const targetMenu = (navKey === 'profile' ? profileHash('personal') : navKey) as NavigatorOption;
        const shouldScrollToTop = navSectionKey(navKey) !== navSectionKey(currentSelection);
        onNavigationChange(targetMenu, shouldScrollToTop);
        setMobileMenuOpen(false);
    };

    const goToPersonalInfo = () => onNavigationChange(profileHash('personal'));

    const domainSchool = useMemo(() =>
        allSchoolsList?.filter(school => school.val().schoolDb === currentDb.databaseURL)[0]
        , [allSchoolsList]);
    const mySchools = useMemo(() =>
        allSchoolsList?.filter(school => !!userSchools?.[school.key])
        , [allSchools, userSchools]);

    const currentSchoolKey = useMemo(() => {
        return domainSchool?.key;
    }, [domainSchool])

    // Use mobile layout if screen width is 930px or less
    const shouldUseMobileLayout = isMobile;

    const changeSchool = (schoolKey) => {
        const newSchoolDomain = mySchools?.filter(newSchool => newSchool.key === schoolKey)[0]?.val().schoolDomain;
        if (newSchoolDomain)
            window.location.href = `https://${newSchoolDomain}/order`;
        else
            console.error(`Did not find domain: ${newSchoolDomain}`);

        // Close mobile menus
        setMobileMenuOpen(false);
        setIsMobileSelectOpen(false);
    };

    const removeSchool = async () => {
        if (!schoolToRemove || !uid) return;

        try {
            // Remove the school from user's schools list directly
            await mainDatabase.ref(`${userSchoolsPath(uid)}/${schoolToRemove.key}`).remove();

            // Wait a bit longer to catch re-addition by other browser tabs
            setTimeout(async () => {
                try {
                    const snapshot = await mainDatabase.ref(`${userSchoolsPath(uid)}/${schoolToRemove.key}`).once('value');

                    if (snapshot.exists()) {
                        setErrorType('readded');
                        setShowErrorDialog(true);
                    }
                } catch (error) {
                    console.error('Error verifying school removal:', error);
                    setErrorType('network');
                    setShowErrorDialog(true);
                }
            }, 2000); // Wait 2 seconds to catch re-addition

            // Reset state
            setSchoolToRemove(null);
        } catch (error) {
            console.error('Error removing school:', error);
            setErrorType('network');
            setShowErrorDialog(true);
        }
    };

    const handleRemoveSchool = (school: DataSnapshot<Schools>, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent dropdown from closing

        // Close the dropdown first
        setIsSelectOpen(false);


        const schoolName = school.val().schoolName?.[lang] || school.val().schoolName?.el || layoutStrings.unknownSchool;
        setSchoolToRemove({ key: school.key, name: schoolName });
    };

    // Create dynamic mapping from school keys to their corresponding logo files based on school groups
    const schoolKeyToLogoMap = useMemo(() => {
        const mapping: Record<string, string> = {};

        if (schoolGroups?.val()) {
            const groups = schoolGroups.val();
            // For each school group, map all its schools to the group key (which is the logo name)
            Object.entries(groups).forEach(([groupKey, schools]) => {
                Object.keys(schools).forEach(schoolKey => {
                    mapping[schoolKey] = groupKey;
                });
            });
        }

        return mapping;
    }, [schoolGroups]);

    // Get the logo path based on the current school key
    const schoolLogoPath = useMemo(() => {
        if (!currentSchoolKey) return null;
        const logoKey = schoolKeyToLogoMap[currentSchoolKey] || 'ac'; // fallback to 'ac'
        return `/img-assets/school-logos/logo-${logoKey}.png`;
    }, [currentSchoolKey, schoolKeyToLogoMap]);

    const hasMultipleSchools = mySchools?.length > 1;

    const headerSchool = () => {
        return <div className='school'>
            {!!schoolLogoPath && <img
                src={schoolLogoPath}
                style={{
                    height: 'auto',
                    width: 'auto',
                    maxHeight: '60px',
                    maxWidth: '120px',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    margin: 'auto 0'
                }}
                alt=""
            />}
            {hasMultipleSchools ? (
                <Select
                    ref={selectRef}
                    value={currentSchoolKey ?? ''}
                    open={isSelectOpen}
                    onOpen={() => setIsSelectOpen(true)}
                    onClose={() => setIsSelectOpen(false)}
                    onChange={(e) => changeSchool(e.target.value)}
                                         IconComponent={() => (
                         <Box
                           component="img"
                           src="/img-assets/arrow-down.svg"
                           alt=""
                           onClick={(e) => {
                             e.stopPropagation();
                             setIsSelectOpen(!isSelectOpen);
                           }}
                           sx={{
                             width: 12,
                             height: 12,
                             transition: "transform 0.5s ease",
                             transform: isSelectOpen ? "rotate(180deg)" : "rotate(0deg)",
                             cursor: "pointer"
                           }}
                         />
                       )}
                                         sx={{
                         minWidth: 200,
                         marginLeft: '10px',
                         cursor: 'pointer',
                         '& .MuiSelect-select': {
                             fontFamily: 'Roboto',
                             fontSize: '16px',
                             padding: '8px 12px',
                             cursor: 'pointer',
                         },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                        },
                        '& .MuiSelect-icon': {
                            display: 'none'
                        }
                    }}
                    MenuProps={{
                        slotProps: {
                            paper: {
                                sx: {
                                    maxHeight: 300,
                                    zIndex: 1000,
                                    '& .MuiMenuItem-root': {
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '8px 12px'
                                    }
                                }
                            }
                        }
                    }}
                >
                    {mySchools.map(school => {
                        const isCurrentSchool = school.key === currentSchoolKey;
                        const schoolName = school.val().schoolName?.[lang] || school.val().schoolName?.el || '';

                        return (
                            <MenuItem
                                key={school.key}
                                value={school.key}
                                sx={{
                                    backgroundColor: isCurrentSchool ? '#f5f5f5' : 'white',
                                    '&:hover': {
                                        backgroundColor: isCurrentSchool ? '#f5f5f5' : '#f8f9fa'
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: '#f5f5f5',
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5'
                                        }
                                    }
                                }}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%'
                                }}>
                                    <span style={{ color: '#666' }}>{schoolName}</span>
                                    {!isCurrentSchool && (
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveSchool(school, e);
                                            }}
                                            sx={{
                                                color: 'var(--lb_red)',
                                                padding: '2px',
                                                marginLeft: '10px',
                                                '&:hover': {
                                                    backgroundColor: 'var(--lb_red)',
                                                    color: 'white'
                                                }
                                            }}
                                            title={layoutStrings.removeSchoolTooltip}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            </MenuItem>
                        );
                    })}
                </Select>
            ) : (
                <div className='school-text'> {domainSchool?.val()?.schoolName?.[lang] ?? ''}</div>
            )}
        </div>
    }

    const headerLogo = () => {
        return <div className='header-logo'>
            <img className='logo' src='/img-assets/logo-light.svg' alt={commonStrings.lunchboxLogoAlt} />
        </div>
    }

    // Basket icon + pending-days badge (multi-day checkout)
    const basketButton = () => (
        <IconButton className="basket-header-button" onClick={openBasket} aria-label={basketStrings.basketAriaLabel}>
            <Badge badgeContent={totalBasketDays} color="error" overlap="circular">
                <ShoppingBasketOutlined />
            </Badge>
        </IconButton>
    );

    const mobileMenu = () => {
        return (
            <Drawer
                anchor="left"
                open={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 280,
                        backgroundColor: '#fff',
                        padding: '20px 0'
                    }
                }}
            >
                <div className="mobile-menu-content">
                    {/* School selector at top */}
                    {hasMultipleSchools && (
                        <div className="mobile-school-selector">
                            <div className="mobile-school-dropdown" onClick={() => setIsMobileSelectOpen(!isMobileSelectOpen)}>
                                <span className="mobile-school-text">
                                    {domainSchool?.val()?.schoolName?.[lang] ?? layoutStrings.demoSchoolFallback}
                                </span>
                                <img
                                    src="/img-assets/arrow-down.svg"
                                    alt=""
                                    className={`mobile-school-arrow ${isMobileSelectOpen ? 'rotated' : ''}`}
                                />
                            </div>

                            {isMobileSelectOpen && (
                                <div className="mobile-school-dropdown-menu">
                                    {mySchools.map(school => {
                                        const isCurrentSchool = school.key === currentSchoolKey;
                                        const schoolName = school.val().schoolName?.[lang] || school.val().schoolName?.el || '';

                                        return (
                                            <div
                                                key={school.key}
                                                className={`mobile-school-option ${isCurrentSchool ? 'current' : ''}`}
                                                onClick={() => {
                                                    if (!isCurrentSchool) {
                                                        changeSchool(school.key);
                                                    }
                                                    setIsMobileSelectOpen(false);
                                                }}
                                            >
                                                <span className="mobile-school-option-text">{schoolName}</span>
                                                {!isCurrentSchool && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveSchool(school, e);
                                                            setIsMobileSelectOpen(false);
                                                        }}
                                                        sx={{
                                                            color: 'var(--lb_red)',
                                                            padding: '2px',
                                                            marginLeft: '10px',
                                                            '&:hover': {
                                                                backgroundColor: 'var(--lb_red)',
                                                                color: 'white'
                                                            }
                                                        }}
                                                        title={layoutStrings.removeSchoolTooltip}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {navigationItems.map((item) => (
                        <div
                            key={item.key}
                            className={`mobile-menu-item ${currentSelection === item.key ? 'selected' : ''}`}
                            onClick={() => handleNavigationClick(item.key)}
                        >
                            <img src={item.icon} alt="" className="mobile-menu-icon" />
                            <span className="mobile-menu-text">
                                {item.label}
                            </span>
                            {item.showNewBadge && <div className="ntf-box">{commonStrings.newBadge}</div>}
                            {(item.badgeCount ?? 0) > 0 && <div className='ntf-box ntf-box-count'>{item.badgeCount}</div>}
                        </div>
                    ))}

                    <div className="mobile-gefsinus-nav">
                        <GefsinusNavPanel />
                    </div>

                    {/* Language selector at bottom */}
                    {showLangToggle && (
                        <div className="mobile-language-selector">
                            <img src="/img-assets/translate.svg" alt="" className="mobile-menu-icon" />
                            <LangToggle className="mobile-lang" />
                        </div>
                    )}
                </div>
            </Drawer>
        );
    };

    return (
        <>
            <div className="header">
                {shouldUseMobileLayout ? (
                    // Mobile header layout
                    (<>
                        <IconButton
                            className="mobile-menu-button"
                            onClick={() => {
                                setMobileMenuOpen(true);
                                setIsMobileSelectOpen(false);
                            }}
                            aria-label={hasMobileNavNotifications ? layoutStrings.menuOpenWithUpdates : layoutStrings.menuOpen}
                            sx={{
                                color: 'var(--lb_green)',
                                padding: '8px',
                                marginLeft: '16px',
                            }}
                        >
                            <Badge color="error" variant="dot" overlap="circular" invisible={!hasMobileNavNotifications}>
                                <MenuIcon />
                            </Badge>
                        </IconButton>
                        <div className="mobile-header-center">
                            <div className="mobile-logo">
                                <img src="/img-assets/logo-light.svg" alt={commonStrings.mobileLogoAlt} className="mobile-logo-img" />
                            </div>
                        </div>
                        <div className="mobile-header-right">
                            {basketButton()}
                            {showHeaderBalance && (
                                <span
                                    className="mobile-balance"
                                    onClick={onRefill}
                                    style={{ color: balance < 0 ? 'var(--red_error)' : undefined }}
                                >
                                    {formatPriceEuro(balance, lang)}
                                </span>
                            )}
                            {photoURL ? (
                                <img
                                    className="mobile-profile-pic"
                                    src={photoURL}
                                    onClick={goToPersonalInfo}
                                    alt={commonStrings.profileImageAlt}
                                />
                            ) : (
                                <span className="mobile-profile-letters" onClick={goToPersonalInfo}>
                                    {(profile?.firstName?.charAt(0) || '') + (profile?.lastName?.charAt(0) || '')}
                                </span>
                            )}
                        </div>
                    </>)
                ) : (
                    // Desktop header layout (unchanged)
                    (<>
                        {headerSchool()}
                        {headerLogo()}
                        <div className='header-profile'>
                            <div className='header-balance'>
                                <LangToggle edgeStyles={{ first: { marginLeft: '20px' }, last: { marginRight: '40px' } }} />
                                {basketButton()}
                                {showHeaderBalance && (
                                    <span className='balance-text-header' onClick={onRefill} style={{ color: balance < 0 ? 'var(--red_error)' : 'inherit' }}>
                                        {formatPriceEuro(balance, lang)}
                                    </span>
                                )}
                            </div>
                            {photoURL &&
                                <img className='profile-pic' src={photoURL} onClick={goToPersonalInfo} alt={commonStrings.profileImageAlt} />
                                || <span className="profileImgLetters" onClick={goToPersonalInfo} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToPersonalInfo(); } }}>{(profile?.firstName?.charAt(0) || '') + (profile?.lastName?.charAt(0) || '')}</span>
                            }
                        </div>
                    </>)
                )}
            </div>
            {mobileMenu()}
            {schoolToRemove && (
                <YesNoDlg
                    title={layoutStrings.removeSchoolTitle}
                    msg={layoutStrings.removeSchoolMsg.replace('{0}', schoolToRemove.name)}
                    yes={commonStrings.removeSchoolAction}
                    no={commonStrings.cancel2}
                    onYes={removeSchool}
                    onNo={() => setSchoolToRemove(null)}
                />
            )}
            {showErrorDialog && (
                <OkDlg
                    title={commonStrings.errorTitle}
                    msg={errorType === 'readded' ? layoutStrings.readdedErrorMsg : layoutStrings.networkErrorMsg}
                    onClose={() => {
                        setShowErrorDialog(false);
                        setErrorType(null);
                    }}
                />
            )}
        </>
    );
}
